/**
 * Decides whether a slide has drifted far enough from the text its ideas were generated from that
 * those ideas no longer represent it.
 *
 * The baseline is free: regenerateIdeas already persists the prompt it used as `metadata.slidePrompt`,
 * so this is a text comparison rather than another AI call.
 *
 * Deliberately dependency-free so the rule is testable on its own.
 */

// Below this, treat the slide as materially unchanged. Fixing a typo or a word should not invalidate
// a set of ideas the user is still choosing between.
const STALE_DIVERGENCE_THRESHOLD = 0.25;

// Markdown scaffolding and visual-idea annotations are noise for this comparison — what matters is
// whether the words changed.
function tokenize(text) {
    return (text ?? "")
        .toLowerCase()
        .replace(/^\s*visual idea:.*$/gim, " ")
        .replace(/[#*_>`|-]+/g, " ")
        .split(/[^\p{L}\p{N}%$.]+/u)
        .map(token => token.replace(/^\.+|\.+$/g, ""))
        .filter(Boolean);
}

/**
 * Symmetric difference over token multisets, normalized by the larger side. 0 means identical wording,
 * 1 means nothing in common. Counting multisets rather than sets means repeated words and reordering
 * of duplicated phrases still register.
 */
export function getTextDivergence(before, after) {
    const beforeTokens = tokenize(before);
    const afterTokens = tokenize(after);

    if (!beforeTokens.length && !afterTokens.length) {
        return 0;
    }
    if (!beforeTokens.length || !afterTokens.length) {
        return 1;
    }

    const counts = new Map();
    for (const token of beforeTokens) {
        counts.set(token, (counts.get(token) ?? 0) + 1);
    }

    let shared = 0;
    for (const token of afterTokens) {
        const remaining = counts.get(token) ?? 0;
        if (remaining > 0) {
            counts.set(token, remaining - 1);
            shared++;
        }
    }

    const largest = Math.max(beforeTokens.length, afterTokens.length);
    return Math.min(1, Math.max(0, 1 - (shared / largest)));
}

/**
 * @param currentPrompt   slide text right now, from buildPromptFromSlide(canvas)
 * @param generatedPrompt slide text when the ideas were generated, from metadata.slidePrompt
 * @param hasIdeas        whether there are any ideas to be stale in the first place
 * @param hasHumanEdits   whether the user has actually changed the slide since AI last wrote it
 */
export function getIdeasFreshness({ currentPrompt, generatedPrompt, hasIdeas = true, hasHumanEdits = true }) {
    if (!hasIdeas) {
        return { isStale: false, divergence: 0 };
    }

    // Text divergence on its own cannot tell an edit from a rendering difference. `generatedPrompt`
    // is the prompt the ideas were generated *from*, while `currentPrompt` is the slide as it was
    // rendered afterwards — enough words move between those two that a freshly generated slide can
    // clear the threshold with nobody having touched it. So a human edit is a precondition, not a
    // corroborating signal: without one there is nothing for the user to refresh.
    if (!hasHumanEdits) {
        return { isStale: false, divergence: 0 };
    }

    // No recorded baseline: the ideas predate this tracking, or were generated from a prompt we never
    // stored. Nothing to compare against, so don't cry wolf.
    if (!generatedPrompt?.trim()) {
        return { isStale: false, divergence: 0 };
    }

    const divergence = getTextDivergence(generatedPrompt, currentPrompt);
    return { isStale: divergence >= STALE_DIVERGENCE_THRESHOLD, divergence };
}

export { STALE_DIVERGENCE_THRESHOLD };
